/**
 * <quick-view> — a product in a modal (bottom sheet on mobile, centred dialog from
 * 768px). Markup and wording: snippets/upsell-products.liquid; data:
 * /products/<handle>?view=quickview (templates/product.quickview.liquid).
 *
 * Opens from any a[data-quick-view] inside its scope (the parent element — the
 * upsell carousel); a modified click (new tab, etc.) still navigates. Product data is
 * prefetched on hover / focus and cached (shared, capped). Sizes show as the product
 * page's rows, a sibling colour of a combined listing loads in place, and add to cart
 * opens the mini cart.
 *
 * Lifecycle: listeners are registered with one AbortController and removed in
 * disconnectedCallback. Each render first releases the content it replaces (releaseBody).
 */
if (!customElements.get('quick-view')) {
  const { escapeHtml, productPath, closeDialog, cancelClose, scrollToSlide } = window.themeUtils;

  const CACHE_LIMIT = 20;
  const CLOSE_FALLBACK_MS = 400;
  const SIZE_SORT_UNKNOWN = Number.MAX_SAFE_INTEGER;

  // Shared by every instance: product data survives a section re-render.
  const cache = new Map();

  const ICONS = {
    in: '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false"><circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M5 8.2l2 2L11 6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    pre: '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false"><circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M8 4.5V8l2.3 1.6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    out: '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false"><circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.3"/><path d="M5.5 5.5l5 5M10.5 5.5l-5 5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    truck: '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M2.75 6.75h11v9.5h-11zM13.75 9.75h3.6l2.9 3.1v3.4h-6.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><circle cx="7" cy="17.25" r="1.75" fill="#fff" stroke="currentColor" stroke-width="1.5"/><circle cx="17" cy="17.25" r="1.75" fill="#fff" stroke="currentColor" stroke-width="1.5"/></svg>',
    star: '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><path d="M8 1.5l1.9 4.1 4.4.5-3.3 3 .9 4.4L8 11.3l-3.9 2.2.9-4.4-3.3-3 4.4-.5z" fill="currentColor"/></svg>',
    cart: '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M6 7h12l-1 13H7L6 7z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M9 9V6a3 3 0 0 1 6 0v3" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>',
    prev: '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    next: '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false"><path d="M10 3L5 8l5 5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    arrow: '<svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false"><path d="M10 3L5 8l5 5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  };

  // Product data for a URL, fetched once and kept (least recently used dropped first).
  const loadProduct = (url) => {
    const path = productPath(url);
    if (cache.has(path)) {
      const hit = cache.get(path);
      cache.delete(path);
      cache.set(path, hit);
      return hit;
    }
    // No "Accept: application/json" header: with it Shopify ignores ?view= and answers
    // with its own product JSON ({ product: … }) instead of this template.
    const request = fetch(`${path}?view=quickview`, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((product) => {
        if (!Array.isArray(product?.variants) || !product.variants.length) throw new Error('Unexpected quick view data');
        return product;
      })
      .catch((error) => {
        cache.delete(path);
        throw error;
      });
    cache.set(path, request);
    if (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);
    return request;
  };

  // Sizes smallest first, by area (80*150 before 160*230), as on the product page.
  const sizeArea = (value) => {
    const match = String(value).match(/(\d+(?:\.\d+)?)\s*[*x×]\s*(\d+(?:\.\d+)?)/);
    return match ? Number(match[1]) * Number(match[2]) : SIZE_SORT_UNKNOWN;
  };

  const stockOf = (variant) => (!variant || !variant.available ? 'out' : variant.preorder ? 'pre' : 'in');

  class QuickView extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.body = this.dialog?.querySelector('[data-qv-body]');
      this.scope = this.parentElement;
      if (!this.dialog || !this.body || !this.scope) return;
      try {
        this.t = JSON.parse(this.querySelector('[data-qv-labels]')?.textContent || '{}');
      } catch (e) {
        this.t = {};
      }
      this.requestId = 0;

      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.scope.addEventListener('click', (e) => this.onTriggerClick(e), { signal });
      this.scope.addEventListener('pointerover', (e) => this.prefetch(e), { passive: true, signal });
      this.scope.addEventListener('focusin', (e) => this.prefetch(e), { signal });
      this.dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.close();
      }, { signal });
      this.dialog.addEventListener('close', () => document.documentElement.classList.remove('qv-open'), { signal });
      this.dialog.addEventListener('click', (e) => this.onDialogClick(e), { signal });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.requestId += 1;
      if (this.dialog?.open) this.dialog.close();
      this.releaseBody();
      document.documentElement.classList.remove('qv-open');
    }

    /**
     * The content about to be replaced (another product, another colour, the error, removal):
     * its images stopped and its nodes unlinked, as on the product page (HTMLUpdateUtility.shatter).
     * An off-screen gallery slide is a lazy image that never starts loading, and Chrome keeps such
     * a pending image — and through it the whole old body — alive: ~450 nodes per open stayed.
     */
    releaseBody() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
      this.track = null;
      this.dots = [];
      if (!this.body?.firstChild) return;
      this.body.querySelectorAll('img').forEach((img) => {
        img.removeAttribute('srcset');
        img.removeAttribute('src');
      });
      const discarded = document.createDocumentFragment();
      while (this.body.firstChild) discarded.appendChild(this.body.firstChild);
      if (typeof HTMLUpdateUtility !== 'undefined') HTMLUpdateUtility.shatter(discarded);
    }

    /* ---------- triggers ---------- */

    onTriggerClick(event) {
      const link = event.target.closest('a[data-quick-view]');
      if (!link || event.defaultPrevented) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open(link.href, link);
    }

    prefetch(event) {
      const link = event.target.closest('a[data-quick-view]');
      if (link) loadProduct(link.href).catch(() => {});
    }

    /* ---------- dialog ---------- */

    async open(url, opener) {
      this.renderSkeleton(opener);
      cancelClose(this.dialog);
      if (!this.dialog.open) {
        this.dialog.showModal();
        document.documentElement.classList.add('qv-open');
      }
      // A link to one variant (?variant=…, e.g. a search for its SKU) opens on that variant.
      const variantId = /^https?:|^\//.test(url) ? new URL(url, window.location.origin).searchParams.get('variant') : null;
      await this.show(url, variantId ? { variantId: Number(variantId) } : {});
    }

    async show(url, keep = {}) {
      const requestId = ++this.requestId;
      this.body.setAttribute('aria-busy', 'true');
      try {
        const product = await loadProduct(url);
        if (requestId !== this.requestId || !this.dialog.open) return;
        this.render(product, keep);
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.releaseBody();
        this.body.innerHTML = `
          <div class="qv__error" role="alert">
            <p>${escapeHtml(this.t.loadError)}</p>
            <a class="qv__link" href="${escapeHtml(productPath(url))}">${escapeHtml(this.t.fullDetails)} ${ICONS.arrow}</a>
          </div>`;
      } finally {
        if (requestId === this.requestId) this.body.removeAttribute('aria-busy');
      }
    }

    close() {
      if (!this.dialog.open) return;
      this.requestId += 1; // drop any response still on its way
      closeDialog(this.dialog, { animated: this.dialog.querySelector('.qv__panel'), ms: CLOSE_FALLBACK_MS });
    }

    onDialogClick(event) {
      if (event.target.closest('[data-qv-close]')) return this.close();
      // A click on the backdrop lands on the <dialog> itself, outside the panel.
      if (event.target === this.dialog) {
        const r = this.dialog.querySelector('.qv__panel').getBoundingClientRect();
        const inside = event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom;
        if (!inside) this.close();
        return;
      }
      const option = event.target.closest('[data-qv-option]');
      if (option) return this.selectOption(option);
      const step = event.target.closest('[data-qv-step]');
      if (step) return this.stepImage(Number(step.dataset.qvStep));
      if (event.target.closest('[data-qv-add]')) this.addToCart();
    }

    /* ---------- rendering ---------- */

    renderSkeleton(opener) {
      const image = opener?.querySelector('img');
      const title = opener?.querySelector('.upsell-card__name')?.textContent.trim() || '';
      this.releaseBody();
      this.body.innerHTML = `
        <div class="qv__layout is-loading">
          <div class="qv__media">
            <div class="qv__slide qv__slide--skeleton">
              ${image ? `<img src="${escapeHtml(image.currentSrc || image.src)}" alt="" class="qv__img">` : ''}
            </div>
          </div>
          <div class="qv__info">
            <h2 class="qv__title" id="QuickViewTitle">${escapeHtml(title)}</h2>
            <div class="qv__skeleton qv__skeleton--price"></div>
            <div class="qv__skeleton qv__skeleton--line"></div>
            <div class="qv__skeleton qv__skeleton--block"></div>
            <div class="qv__skeleton qv__skeleton--block"></div>
          </div>
        </div>`;
    }

    render(product, keep = {}) {
      const variants = product.variants;
      // Only values this product has a variant for (combined listings list their siblings'
      // sizes too) — except values that lead to a sibling product (another colour).
      const options = (product.options || []).map((option, index) => {
        const values = option.values.filter((value) => value.productUrl || variants.some((v) => v.options[index] === value.value));
        if (option.isSize) values.sort((a, b) => sizeArea(a.value) - sizeArea(b.value));
        return { ...option, index, values };
      });

      // Another colour lands on its first in-stock size (not a pre-order), as on the product page.
      const variant =
        variants.find((v) => v.id === keep.variantId) ||
        (keep.firstInStock &&
          [...variants].sort((a, b) => sizeArea(a.options.join(' ')) - sizeArea(b.options.join(' '))).find((v) => stockOf(v) === 'in')) ||
        variants.find((v) => v.id === product.selectedVariantId) ||
        variants.find((v) => v.available) ||
        variants[0];

      this.state = { product, options, variant };
      const images = product.images || [];
      const slides = images.length
        ? images
            .map(
              (image, i) => `
          <div class="qv__slide" data-image-id="${image.id}" role="group" aria-label="${escapeHtml(this.t.image)} ${i + 1} / ${images.length}">
            <img class="qv__img" src="${escapeHtml(image.src)}" srcset="${escapeHtml(image.srcset)}"
              sizes="(min-width: 768px) 460px, 100vw" alt="${escapeHtml(image.alt)}"
              ${i === 0 ? 'fetchpriority="high"' : 'loading="lazy"'} decoding="async">
          </div>`
            )
            .join('')
        : '<div class="qv__slide qv__slide--skeleton"></div>';

      this.releaseBody();
      this.body.innerHTML = `
        <div class="qv__layout">
          <div class="qv__media">
            <div class="qv__track" data-qv-track tabindex="0">${slides}</div>
            <span class="qv__badge" data-qv-badge hidden></span>
            ${
              images.length > 1
                ? `<button type="button" class="qv__nav qv__nav--prev" data-qv-step="-1" aria-label="${escapeHtml(this.t.prev)}">${ICONS.prev}</button>
                   <button type="button" class="qv__nav qv__nav--next" data-qv-step="1" aria-label="${escapeHtml(this.t.next)}">${ICONS.next}</button>
                   <div class="qv__dots" aria-hidden="true">${images.map((_, i) => `<span class="qv__dot${i === 0 ? ' is-active' : ''}"></span>`).join('')}</div>`
                : ''
            }
          </div>
          <div class="qv__info">
            <h2 class="qv__title" id="QuickViewTitle">${escapeHtml(product.title)}</h2>
            <div class="qv__meta">
              ${product.rating ? `<a class="qv__rating" href="${escapeHtml(product.url)}#product-reviews" aria-label="${escapeHtml((this.t.ratingOf || '').replace('{avg}', product.rating.average))}">${ICONS.star}<b>${escapeHtml(String(product.rating.average))}</b><span>(${product.rating.count})</span><span class="visually-hidden"> ${escapeHtml(this.t.reviews || '')}</span></a>` : ''}
              <span class="qv__sku" data-qv-sku></span>
            </div>
            <div class="qv__price" data-qv-price></div>
            <p class="qv__stock" data-qv-stock></p>
            <p class="qv__delivery" data-qv-delivery hidden></p>
            <div class="qv__options">${options.map((option) => this.optionMarkup(option)).join('')}</div>
            <p class="qv__message" data-qv-message role="alert" hidden></p>
          </div>
          <div class="qv__actions">
            <div class="qv__buy">
              <div class="qv__buy-summary" aria-live="polite">
                <span class="qv__buy-price" data-qv-buy-price></span>
                <span class="qv__buy-size" data-qv-cta-size></span>
              </div>
              <button type="button" class="qv__cta" data-qv-add>
                <span class="qv__cta-text" data-qv-cta-label>${escapeHtml(this.t.addToCart)}</span>${ICONS.cart}
                <span class="qv__cta-spinner" aria-hidden="true"></span>
              </button>
            </div>
            <a class="qv__link" data-qv-full href="${escapeHtml(product.url)}">${escapeHtml(this.t.fullDetails)} ${ICONS.arrow}</a>
          </div>
        </div>`;

      this.track = this.body.querySelector('[data-qv-track]');
      this.dots = [...this.body.querySelectorAll('.qv__dot')];
      // A plain listener: the track is replaced with the body on the next render and the
      // listener goes with it (tying it to the component's signal would keep old tracks alive).
      this.track?.addEventListener('scroll', () => this.onTrackScroll(), { passive: true });
      this.update({ scrollToImage: true, instant: true });
    }

    optionMarkup({ name, isSize, values, index }) {
      const selected = this.state.variant?.options[index];
      const attrs = (v) =>
        `data-qv-option="${index}" data-value="${escapeHtml(v.value)}" aria-pressed="${v.value === selected}"${
          v.productUrl ? ` data-product-url="${escapeHtml(v.productUrl)}"` : ''
        }`;

      if (isSize) {
        return `
          <fieldset class="qv__option">
            <legend class="qv__option-name">${escapeHtml(name)}</legend>
            <div class="qv__sizes">
              ${values
                .map(
                  (v) => `
                <button type="button" class="qv__size" ${attrs(v)}>
                  <span class="qv__size-name"><span class="qv__size-label">${escapeHtml(v.label)}</span>${v.hint ? `<span class="qv__size-hint">${escapeHtml(v.hint)}</span>` : ''}</span>
                  <span class="qv__size-meta" data-qv-size-meta></span>
                  <span class="qv__size-stock" data-qv-size-stock></span>
                </button>`
                )
                .join('')}
            </div>
          </fieldset>`;
      }

      const swatches = values.some((v) => v.swatch);
      return `
        <fieldset class="qv__option">
          <legend class="qv__option-name">${escapeHtml(name)}${swatches ? `: <span data-qv-selected-name="${index}">${escapeHtml(selected || '')}</span>` : ''}</legend>
          <div class="${swatches ? 'qv__swatches' : 'qv__pills'}">
            ${values
              .map((v) =>
                swatches
                  ? `<button type="button" class="qv__swatch" ${attrs(v)} aria-label="${escapeHtml(v.label)}" title="${escapeHtml(v.label)}"
                      style="${v.swatch ? `--qv-swatch: url('${escapeHtml(v.swatch)}')` : ''}"></button>`
                  : `<button type="button" class="qv__pill" ${attrs(v)}>${escapeHtml(v.label)}</button>`
              )
              .join('')}
          </div>
        </fieldset>`;
    }

    /* ---------- selection ---------- */

    selectOption(button) {
      const { product, variant, options } = this.state;
      const index = Number(button.dataset.qvOption);
      const value = button.dataset.value;

      // Another colour of a combined listing is another product: load it in place, keep
      // the size the shopper picked when that product has it, and keep keyboard focus.
      if (button.dataset.productUrl) {
        this.show(button.dataset.productUrl, { firstInStock: true }).then(() => {
          this.body.querySelector(`[data-qv-option="${index}"][data-value="${CSS.escape(value)}"]`)?.focus();
        });
        return;
      }

      const wanted = [...(variant?.options || [])];
      wanted[index] = value;
      this.state.variant =
        product.variants.find((v) => v.options.every((o, i) => o === wanted[i])) ||
        product.variants.find((v) => v.options[index] === value && v.available) ||
        product.variants.find((v) => v.options[index] === value) ||
        variant;
      this.update({ scrollToImage: true });
    }

    variantFor(index, value) {
      const wanted = [...(this.state.variant?.options || [])];
      wanted[index] = value;
      return this.state.product.variants.find((v) => v.options.every((o, i) => o === wanted[i])) || null;
    }

    // The estimated delivery day, by the product page's rules (assets/delivery-estimate.js);
    // the Liquid day on the element when the engine is missing.
    deliveryDate() {
      const engine = window.DeliveryEstimate;
      const date = engine?.forElement(this) ||
        (/^\d{4}-\d{2}-\d{2}$/.test(this.dataset.deliveryFallback || '') ? new Date(`${this.dataset.deliveryFallback}T00:00:00Z`) : null);
      if (!date) return null;
      const label = engine
        ? engine.format(date)
        : date.toLocaleDateString('he-IL', { timeZone: 'UTC', day: '2-digit', month: '2-digit', year: 'numeric' });
      return { label, iso: date.toISOString().slice(0, 10), weekday: (this.t.weekdays || [])[date.getUTCDay()] || '' };
    }

    stockText(state, variant) {
      return state === 'in' ? this.t.inStock : state === 'pre' ? this.t.preorder : variant ? this.t.soldOut : this.t.unavailable;
    }

    update({ scrollToImage = false, instant = false } = {}) {
      const { variant, options } = this.state;
      const body = this.body;
      const state = stockOf(variant);

      body.querySelector('[data-qv-price]').innerHTML = variant
        ? `<span class="qv__price-now${variant.compare ? ' is-sale' : ''}">${escapeHtml(variant.price)}</span>
           ${variant.compare ? `<s class="qv__price-was">${escapeHtml(variant.compare)}</s>` : ''}
           ${variant.discount ? `<span class="qv__price-off">${variant.discount}% ${escapeHtml(this.t.discount)}</span>` : ''}`
        : '';

      const badge = body.querySelector('[data-qv-badge]');
      if (badge) {
        badge.hidden = !variant?.discount;
        badge.textContent = variant?.discount ? `${variant.discount}% ${this.t.discount}` : '';
      }

      const stock = body.querySelector('[data-qv-stock]');
      stock.className = `qv__stock is-${state}`;
      stock.innerHTML = `${ICONS[state]}${escapeHtml(this.stockText(state, variant))}${
        state === 'pre' ? ` · ${escapeHtml(this.t.preorderUntil)} <strong>${escapeHtml(variant.preorder)}</strong>` : ''
      }`;

      // In stock: when it arrives (a pre-order already carries its date, sold out has none).
      const delivery = body.querySelector('[data-qv-delivery]');
      const day = state === 'in' ? this.deliveryDate() : null;
      delivery.hidden = !day;
      delivery.innerHTML = day
        ? `<span class="qv__delivery-icon">${ICONS.truck}</span>
           <span class="qv__delivery-text">${escapeHtml(this.t.deliveryUntil || '')} <time datetime="${day.iso}"><strong>${escapeHtml(day.label)}</strong>${day.weekday ? ` <span class="qv__delivery-day">(${escapeHtml(day.weekday)})</span>` : ''}</time></span>`
        : '';

      body.querySelectorAll('[data-qv-option]').forEach((button) => {
        const index = Number(button.dataset.qvOption);
        const value = button.dataset.value;
        button.setAttribute('aria-pressed', String(variant?.options[index] === value));
        if (button.dataset.productUrl) return;
        const candidate = this.variantFor(index, value);
        const candidateState = stockOf(candidate);
        button.classList.toggle('is-unavailable', candidateState === 'out');
        if (!button.classList.contains('qv__size')) return;
        button.querySelector('[data-qv-size-meta]').innerHTML = candidate
          ? `${candidate.discount ? `<span class="qv__size-off">${candidate.discount}%-</span>` : ''}<span class="qv__size-prices">${candidate.compare ? `<s class="qv__size-was"><span class="visually-hidden">במקום </span>${escapeHtml(candidate.compare)}</s>` : ''}<span class="qv__size-price">${escapeHtml(candidate.price)}</span></span>`
          : '';
        const sizeStock = button.querySelector('[data-qv-size-stock]');
        sizeStock.className = `qv__size-stock is-${candidateState}`;
        sizeStock.innerHTML = `${ICONS[candidateState]}${escapeHtml(this.stockText(candidateState, candidate))}`;
      });
      options.forEach((option) => {
        const nameEl = body.querySelector(`[data-qv-selected-name="${option.index}"]`);
        if (nameEl) nameEl.textContent = option.values.find((v) => v.value === variant?.options[option.index])?.label || '';
      });

      // SKU of the chosen size, with the product page's copy element (assets/SkuCopy.js).
      const skuSlot = body.querySelector('[data-qv-sku]');
      if (skuSlot) {
        const sku = variant?.sku || '';
        skuSlot.hidden = !sku;
        const current = skuSlot.querySelector('sku-copy');
        if (current && 'skuValue' in current) current.skuValue = sku; // setter only: `in`, not typeof
        else if (current) current.setAttribute('sku', sku);
        else if (sku) {
          const el = document.createElement('sku-copy');
          el.setAttribute('sku', sku);
          if (this.dataset.skuIcon) el.setAttribute('icon', this.dataset.skuIcon);
          skuSlot.replaceChildren(el);
        }
      }

      const cta = body.querySelector('[data-qv-add]');
      cta.disabled = state === 'out';
      // The chosen size on the button, as the product page's sticky bar shows it.
      const sizeOption = options.find((option) => option.isSize);
      const sizeValue = sizeOption && variant ? sizeOption.values.find((v) => v.value === variant.options[sizeOption.index]) : null;
      const ctaSize = body.querySelector('[data-qv-cta-size]');
      if (ctaSize) {
        ctaSize.textContent = sizeValue?.label || '';
        ctaSize.hidden = !sizeValue || state === 'out';
      }
      body.querySelector('[data-qv-cta-label]').textContent = state === 'out' ? this.stockText(state, variant) : this.t.addToCart;
      const buyPrice = body.querySelector('[data-qv-buy-price]');
      if (buyPrice) {
        buyPrice.innerHTML = variant
          ? `<b class="${variant.compare ? 'is-sale' : ''}">${escapeHtml(variant.price)}</b>${variant.compare ? `<s>${escapeHtml(variant.compare)}</s>` : ''}`
          : '';
      }
      body.querySelector('[data-qv-full]').href = `${this.state.product.url}${variant ? `?variant=${variant.id}` : ''}`;
      this.hideMessage();

      if (scrollToImage && variant?.imageId) this.scrollToImage(variant.imageId, instant);
    }

    /* ---------- gallery ---------- */

    slides() {
      return this.track ? [...this.track.children] : [];
    }

    scrollToImage(imageId, instant) {
      const slide = this.slides().find((s) => Number(s.dataset.imageId) === imageId);
      if (slide) scrollToSlide(this.track, slide, instant);
    }

    currentSlide() {
      return this.track ? Math.round(Math.abs(this.track.scrollLeft) / (this.track.clientWidth || 1)) : 0;
    }

    stepImage(direction) {
      const slides = this.slides();
      if (!slides.length) return;
      scrollToSlide(this.track, slides[(this.currentSlide() + direction + slides.length) % slides.length]);
    }

    onTrackScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        const active = this.currentSlide();
        this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === active));
      });
    }

    /* ---------- cart ---------- */

    async addToCart() {
      const variant = this.state?.variant;
      const cta = this.body.querySelector('[data-qv-add]');
      if (!variant?.available || cta.classList.contains('is-loading')) return;

      cta.classList.add('is-loading');
      cta.disabled = true;
      cta.setAttribute('aria-busy', 'true');
      this.hideMessage();

      try {
        const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variant.id, quantity: 1 }],
            ...(window.CartManagerInstance ? { sections: window.CartManagerInstance.sectionId } : {}),
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.description || data.message || this.t.error);

        this.close();
        if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-view', productVariantId: variant.id });
        }
        const cartManager = window.CartManagerInstance;
        if (cartManager) {
          cartManager.showAdded(data);
        } else {
          window.location.href = window.routes?.cart_url || '/cart';
        }
      } catch (error) {
        this.showMessage(error.message || this.t.error);
      } finally {
        cta.classList.remove('is-loading');
        cta.removeAttribute('aria-busy');
        cta.disabled = stockOf(this.state?.variant) === 'out';
      }
    }

    showMessage(text) {
      const message = this.body.querySelector('[data-qv-message]');
      if (!message) return;
      message.textContent = text;
      message.hidden = false;
    }

    hideMessage() {
      const message = this.body.querySelector('[data-qv-message]');
      if (message) message.hidden = true;
    }
  }

  customElements.define('quick-view', QuickView);
}
